<i18n>
{
	"en": {
		"newAddress": "Add address",
		"deleteAddress": "Remove address | Remove address | Remove addresses",
		"noAddressesFound": "No Addresses...",
		"header": "Addresses ({count})",
		"name": "Name",
		"address": "Street",
		"street": "Street",
		"street3": "Street 2",
		"street4": "Street 3",
		"street5": "Street 4",
		"street2": "Street 5",
		"postalPlace": "Postal Place",
		"postalCode": "Postal Code",
		"countryCode": "Country",
		"size": "Size",
		"editedDate": "Edited Date",
		"updatedAddress": "Trip address updated",
		"failedToAdd": "Failed to add new address",
		"failedToUpdate": "Failed to update address",
		"failedToFetch": "Failed to get address data",
		"failedToDelete": "Failed to delete address",
		"deletedAddresses": "Deleted address | Deleted address | Deleted {count} addresses",
		"terminal": "Terminal",
		"contactPhone": "Phone Number",
		"contactEmail": "Email",
		"marks": "Marks",
		"refresh": "Refresh",
		"gs1LocationNumber": "GS1 Location Number",
		"contactName": "Contact Name",
		"website": "Website",
		"customsCreditNumber": "Customs Credit Number",
		"vat": "MVA",
		"voec": "VOEC",
		"organizationNumber": "Organization Number",
		"exporterNumber": "Exporter Number",
		"eori": "EORI",
		"customerNumber": "Customer Number",
		"vendorNumber": "Vendor Number",
		"position":"Position"
	},
	"no": {
		"newAddress": "Legg til adresse",
		"deleteAddress": "Fjern adresse | Fjern adresse | Fjern adresser",
		"noAddressesFound": "Ingen adresser...",
		"header": "Adresser ({count})",
		"name": "Navn",
		"address": "Gate",
		"street": "Gate",
		"street2": "Gate 2",
		"street3": "Gate 3",
		"street4": "Gate 4",
		"street5": "Gate 5",
		"postalPlace": "Sted",
		"postalCode": "Postnummer",
		"countryCode": "Land",
		"size": "Størrelse",
		"editedDate": "Endret dato",
		"updatedAddress": "Adresse oppdatert",
		"failedToAdd": "Kunne ikke legge til ny adresse",
		"failedToUpdate": "Kunne ikke oppdatere adresse",
		"failedToFetch": "Kunne ikke hente adresse data",
		"failedToDelete": "Kunne ikke slette adresse",
		"deletedAddresses": "Adressen ble slettet | Adressen ble slettet | Slettet {count} adresser",
		"terminal": "Terminal",
		"contactPhone": "Telefon",
		"contactEmail": "E-post",
		"marks": "Merknader",
		"refresh": "Refresh",
		"gs1LocationNumber": "GS1 Locno.",
		"contactName": "Kontakt navn",
		"website": "Nettside",
		"customsCreditNumber": "Tollkredittnummber",
		"vat": "MVA",
		"voec": "VOEC",
		"organizationNumber": "Foretaksnummer",
		"exporterNumber": "Eksportør nummer",
		"eori": "EORI",
		"customerNumber": "Kunde",
		"vendorNumber": "Leverandør",
		"position":"Posisjon"
	}
}
</i18n>
<template>
	<t-vertical-panel class="address-list">

		<div
			slot="header"
			class="address-list-header"
		>
			<t-panel-header
				size="md"
				icon="globe"
				type="bp"
			>
				{{ $tc('header', allGlobalAddresses.length) }}
			</t-panel-header>
		</div>

		<div slot="sub-header" class="address-list-sub-header">
			<div class="left">
				<t-button
					:busy="loading.fetchAllGlobalAddresses"
					iconBefore="refresh"
					appearance="primary"
					@click="fetchAllGlobalAddresses"
				>
					{{ $t('refresh') }}
				</t-button>
			</div>
			<div class="right">
				<t-button
					:disabled="selectedRows.length === 0"
					iconBefore="trash"
					appearance="primary"
					intent="danger"
					type="bp"
					@click="onDeleteGlobalAddresses"
				>
					{{ $tc('deleteAddress', selectedRows.length) }}
				</t-button>
				<t-button
					iconBefore="plus"
					appearance="primary"
					@click="onAddGlobalAddress"
				>
					{{ $t('newAddress') }}
				</t-button>
			</div>
		</div>

		<div class="address-list-content">
			<t-grid
				v-if="allGlobalAddresses.length"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="40"
				:items="allGlobalAddresses"
				:selectableMode="['checkbox']"
				localStorageId="globalAddressesSearchList"
				selectable
				@selected="onRowsSelected"
			>
				<template v-slot:position="{ item }">
					<t-icon-button
						icon="map-marker"
						type="bp"
						appearance="minimal"
						disableBoxShadow
						:disabled="!item.latitude || !item.longitude"
						@click="openPositionInGoogleMaps(item)"
					/>
				</template>
				<template v-slot:name="{ item }">
					<t-text-input
						:value="item.name"
						:isInvalid="errors.name && item.id === errors.id"
						@change="updateValue(item, 'name', $event)"
					/>
				</template>
				<template v-slot:street="{ item }">
					<t-text-input
						:value="item.street"
						:isInvalid="errors.street && item.id === errors.id"
						@change="updateValue(item, 'street', $event)"
					/>
				</template>
				<template v-slot:street2="{ item }">
					<t-text-input
						:value="item.street2"
						:isInvalid="errors.street2 && item.id === errors.id"
						@change="updateValue(item, 'street2', $event)"
					/>
				</template>
				<template v-slot:street3="{ item }">
					<t-text-input
						:value="item.street3"
						:isInvalid="errors.street3 && item.id === errors.id"
						@change="updateValue(item, 'street3', $event)"
					/>
				</template>
				<template v-slot:street4="{ item }">
					<t-text-input
						:value="item.street4"
						:isInvalid="errors.street4 && item.id === errors.id"
						@change="updateValue(item, 'street4', $event)"
					/>
				</template>
				<template v-slot:street5="{ item }">
					<t-text-input
						:value="item.street5"
						:isInvalid="errors.street5 && item.id === errors.id"
						@change="updateValue(item, 'street5', $event)"
					/>
				</template>
				<template v-slot:postalPlace="{ item }">
					<t-text-input
						:value="item.postalPlace"
						:isInvalid="errors.postalPlace && item.id === errors.id"
						@change="updateValue(item, 'postalPlace', $event)"
					/>
				</template>
				<template v-slot:postalCode="{ item }">
					<t-text-input
						:value="item.postalCode"
						:isInvalid="errors.postalCode && item.id === errors.id"
						@change="updateValue(item, 'postalCode', $event)"
					/>
				</template>
				<template v-slot:countryCode="{ item }">
					<t-text-input
						:value="item.countryCode.toUpperCase()"
						:isInvalid="errors.countryCode && item.id === errors.id"
						@change="updateValue(item, 'countryCode', $event)"
					/>
				</template>
				<template v-slot:contactEmail="{ item }">
					<t-text-input
						:value="item.contactEmail"
						:isInvalid="errors.contactEmail && item.id === errors.id"
						@change="updateValue(item, 'contactEmail', $event)"
					/>
				</template>
				<template v-slot:contactPhone="{ item }">
					<t-text-input
						:value="item.contactPhone"
						:isInvalid="errors.contactPhone && item.id === errors.id"
						@change="updateValue(item, 'contactPhone', $event)"
					/>
				</template>
				<template v-slot:marks="{ item }">
					<t-text-input
						:value="item.marks"
						:isInvalid="errors.marks && item.id === errors.id"
						@change="updateValue(item, 'marks', $event)"
					/>
				</template>
				<template v-slot:gs1LocationNumber="{ item }">
					<t-text-input
						:value="item.gs1LocationNumber"
						:isInvalid="errors.gs1LocationNumber && item.id === errors.id"
						@change="updateValue(item, 'gs1LocationNumber', $event)"
					/>
				</template>
				<template v-slot:contactName="{ item }">
					<t-text-input
						:value="item.contactName"
						:isInvalid="errors.contactName && item.id === errors.id"
						@change="updateValue(item, 'contactName', $event)"
					/>
				</template>
				<template v-slot:website="{ item }">
					<t-text-input
						:value="item.website"
						:isInvalid="errors.website && item.id === errors.id"
						@change="updateValue(item, 'website', $event)"
					/>
				</template>
				<template v-slot:customsCreditNumber="{ item }">
					<t-text-input
						:value="item.customsCreditNumber"
						:isInvalid="errors.customsCreditNumber && item.id === errors.id"
						@change="updateValue(item, 'customsCreditNumber', $event)"
					/>
				</template>
				<template v-slot:vat="{ item }">
					<div style="text-align: center">
						<div style="display: inline-block">
							<t-checkbox
								:value="item.vat"
								@change="updateValue(item, 'vat', $event)"
							/>
						</div>
					</div>
				</template>
				<template v-slot:voec="{ item }">
					<t-text-input
						:value="item.voec"
						:isInvalid="errors.voec && item.id === errors.id"
						@change="updateValue(item, 'voec', $event)"
					/>
				</template>
				<template v-slot:organizationNumber="{ item }">
					<t-text-input
						:value="item.organizationNumber"
						:isInvalid="errors.organizationNumber && item.id === errors.id"
						@change="updateValue(item, 'organizationNumber', $event)"
					/>
				</template>
				<template v-slot:exporterNumber="{ item }">
					<t-text-input
						:value="item.exporterNumber"
						:isInvalid="errors.exporterNumber && item.id === errors.id"
						@change="updateValue(item, 'exporterNumber', $event)"
					/>
				</template>
				<template v-slot:eori="{ item }">
					<t-text-input
						:value="item.eori"
						:isInvalid="errors.eori && item.id === errors.id"
						@change="updateValue(item, 'eori', $event)"
					/>
				</template>
				<template v-slot:customerNumber="{ item }">
					<t-number-input
						:value="item.customerNumber"
						:isInvalid="errors.customerNumber && item.id === errors.id"
						@change="updateValue(item, 'customerNumber', $event)"
					/>
				</template>
				<template v-slot:vendorNumber="{ item }">
					<t-text-input
						:value="item.vendorNumber"
						:isInvalid="errors.vendorNumber && item.id === errors.id"
						@change="updateValue(item, 'vendorNumber', $event)"
					/>
				</template>
			</t-grid>

			<t-no-content-panel v-else :text="$t('noAddressesFound')" icon="order" />

			<t-overlay
				:isShown="loading.fetchAllGlobalAddresses"
				absolute
				loader
			/>
		</div>
	</t-vertical-panel>
</template>

<script>

import {
	mapState,
	mapActions,
} from '@/modules/globalAddresses/store/globalAddresses.store';

export default {
	name: 'GlobalAddressRegistry',
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			selectedRows: []
		};
	},
	computed: {
		...mapState([
			'allGlobalAddresses',
			'loading',
			'errors',
		]),

		columnDefinitions() {
			return [
				{
					field: 'position',
					displayName: this.$t('position'),
					sortable: false,
					filterable: false,
					width: 70,
				},
				{
					field: 'name',
					displayName: this.$t('name'),
				},
				{
					field: 'street',
					displayName: this.$t('street'),
				},
				{
					field: 'street2',
					displayName: this.$t('street2'),
				},
				{
					field: 'street3',
					displayName: this.$t('street3'),
				},
				{
					field: 'street4',
					displayName: this.$t('street4'),
				},
				{
					field: 'street5',
					displayName: this.$t('street5'),
				},
				{
					field: 'postalCode',
					displayName: this.$t('postalCode'),
				},
				{
					field: 'postalPlace',
					displayName: this.$t('postalPlace'),
				},
				{
					field: 'countryCode',
					displayName: this.$t('countryCode'),
				},
				{
					field: 'contactEmail',
					displayName: this.$t('contactEmail'),
				},
				{
					field: 'contactPhone',
					displayName: this.$t('contactPhone'),
				},
				{
					field: 'marks',
					displayName: this.$t('marks'),
				},
				{
					field: 'gs1LocationNumber',
					displayName: this.$t('gs1LocationNumber'),
				},
				{
					field: 'contactName',
					displayName: this.$t('contactName'),
				},
				{
					field: 'website',
					displayName: this.$t('website'),
				},
				{
					field: 'customsCreditNumber',
					displayName: this.$t('customsCreditNumber'),
				},
				{
					field: 'vat',
					displayName: this.$t('vat'),
					width: 48
				},
				{
					field: 'voec',
					displayName: this.$t('voec'),
				},
				{
					field: 'organizationNumber',
					displayName: this.$t('organizationNumber'),
				},
				{
					field: 'exporterNumber',
					displayName: this.$t('exporterNumber'),
				},
				{
					field: 'eori',
					displayName: this.$t('eori'),
				},
				{
					field: 'customerNumber',
					displayName: this.$t('customerNumber'),
				},
				{
					field: 'vendorNumber',
					displayName: this.$t('vendorNumber'),
				}
			];
		}
	},
	created() {
		this.fetchAllGlobalAddresses();
	},
	methods: {
		...mapActions([
			'fetchAllGlobalAddresses',
			'addEmptyGlobalAddress',
			'updateGlobalAddress',
			'deleteGlobalAddresses',
		]),

		onRowsSelected(ids) {
			this.selectedRows = ids;
		},
		async updateValue(globalAddress, key, value ) {
			try {
				await this.updateGlobalAddress({ globalAddress, key, value });
				this.$notify({
					type: 'success',
					title: this.$t('updatedAddress'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdate'),
				});
			}
		},
		async onDeleteGlobalAddresses() {

			let selectedGlobalAddresses = this.$refs.grid.getSelectedItems();

			if (!selectedGlobalAddresses)
				return;

			try {
				await this.deleteGlobalAddresses(selectedGlobalAddresses);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedAddresses', selectedGlobalAddresses.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelete')
				});
			}
		},
		async onAddGlobalAddress() {
			try {
				await this.addEmptyGlobalAddress();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToAdd')
				});
			}
		},
		openPositionInGoogleMaps(data) {
			let url = `https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`;
			let win = window.open(url, '_blank');
			win.focus();
		},
	},
};
</script>

<style lang="scss" scoped>

.address-list {

	.address-list-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.address-list-sub-header {
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: space-between;
		.left {
			display: flex;
			padding: 0px 15px;
			justify-content: flex-start;
			> button {
				margin: 10px;
			}
		}
		.right {
			display: flex;
			padding: 0px 15px;
			justify-content: flex-end;
			> button {
				margin: 10px;
			}
		}
	}

	.address-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px;
	}
}
</style>
